import { useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, X, Check, Copy } from 'lucide-react';

// ── Password Reset Modal ────────────────────────────────────────────────
//
// Shared between Super Admin (reset org owner password) and tenant Admin
// (reset a regular user's password). Caller supplies a `title`, `subject`
// (the user/org being reset) and an `onConfirm(newPassword)` that hits
// the right endpoint.
//
// UX:
//   - Input with show/hide toggle
//   - 8-char minimum client validation
//   - "Generate" button fills a 12-char random string (letters + digits)
//   - Clear warning that the password is one-time visible and must be shared
//     securely
//   - On success, shows the final password with a copy button so admin can
//     pass it to the user

interface Props {
  isOpen: boolean;
  title: string;
  subject: string; // e.g. "org owner laje (laje@x.com)" or user's email
  onConfirm: (newPassword: string) => Promise<void>;
  onClose: () => void;
}

function randomPassword(): string {
  // 12 chars: upper + lower + digits; avoid confusing chars
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  const crypto = globalThis.crypto ?? (window as any).crypto;
  const buf = new Uint8Array(12);
  crypto.getRandomValues(buf);
  for (let i = 0; i < buf.length; i++) out += chars[buf[i] % chars.length];
  return out;
}

export function PasswordResetModal({ isOpen, title, subject, onConfirm, onClose }: Props) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const tooShort = password.length > 0 && password.length < 8;

  const reset = () => {
    setPassword('');
    setShow(false);
    setSaving(false);
    setDone(false);
    setError(null);
    setCopied(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleGenerate = () => {
    setPassword(randomPassword());
    setShow(true);
    setError(null);
  };

  const handleSubmit = async () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(password);
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
        onClick={saving ? undefined : handleClose}
      />
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <KeyRound className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">{title}</h2>
              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[300px]">{subject}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="p-1.5 hover:bg-slate-100 rounded-lg disabled:opacity-50"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        {!done ? (
          <div className="px-6 py-5 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">New Password</label>
                <button
                  type="button"
                  onClick={handleGenerate}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                >
                  Generate
                </button>
              </div>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  placeholder="Min 8 characters"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {tooShort && (
                <p className="text-xs text-red-500 mt-1.5">Password must be at least 8 characters</p>
              )}
              {error && (
                <p className="text-xs text-red-500 mt-1.5">{error}</p>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
              <p className="text-xs text-amber-700">
                ⚠️ The user's active sessions will be signed out. Share the new password
                with them through a secure channel (not email).
              </p>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <Check className="h-5 w-5" />
              <span className="text-sm font-semibold">Password reset successful</span>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">New Password</label>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-800 select-all">
                  {password}
                </code>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-3 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-xs font-semibold"
                >
                  {copied ? (<span className="text-emerald-600">Copied!</span>) : (
                    <span className="flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" /> Copy</span>
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Save this now — it won't be shown again. Share with the user securely.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          {!done ? (
            <>
              <button
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || password.length < 8}
                className="px-5 py-2 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Reset Password
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
